/**
 * Error Dialog component
 * Displays user-friendly error messages with optional recovery actions
 */

import { useEffect } from 'react';
import { Button } from './Button';

interface ErrorDialogProps {
  title?: string;
  message: string;
  onClose: () => void;
  onAction?: () => void;
  actionLabel?: string;
}

export function ErrorDialog({
  title = 'Oops! Something went wrong',
  message,
  onClose,
  onAction,
  actionLabel = 'Help',
}: ErrorDialogProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-panel shadow-xl max-w-md w-full border border-rule">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-del/15 flex items-center justify-center">
              <svg
                aria-hidden="true"
                className="w-6 h-6 text-del"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink">{title}</h3>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="text-dim hover:text-ink transition-colors"
          >
            <svg
              aria-hidden="true"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Message */}
        <div className="px-6 pb-6">
          <p className="text-sm text-dim whitespace-pre-line">{message}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6 pt-0 border-t border-rule">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-ink bg-bg hover:bg-accent-soft transition-colors"
          >
            Close
          </button>
          {onAction && (
            <Button
              onClick={() => {
                onAction();
                onClose();
              }}
              className="flex-1"
            >
              {actionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
